import { useState, useEffect, ReactNode, lazy, Suspense } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, Globe, Menu, ChevronDown, ArrowUp } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Helmet } from 'react-helmet-async';
import { languages, translations } from '../translations';
import Footer from './Footer';

const SearchModal = lazy(() => import('./SearchModal'));

const LanguageIcon = ({ code, className = "w-5 h-5" }: { code: string; className?: string }) => {
  if (code === 'EN') return <Globe className={`${className} text-gray-400`} />;
  if (code === 'JP') return (
    <svg viewBox="0 0 900 600" className={className} role="img" aria-label="Japanese Flag">
      <rect width="900" height="600" fill="#fff" stroke="#eee" strokeWidth="10" />
      <circle cx="450" cy="300" r="180" fill="#bc002d" />
    </svg>
  );
  if (code === 'ID') return (
    <svg viewBox="0 0 3 2" className={className} role="img" aria-label="Indonesian Flag">
      <rect width="3" height="1" fill="#e00011" />
      <rect width="3" height="1" y="1" fill="#fff" stroke="#eee" strokeWidth="0.05" />
    </svg>
  );
  return null;
};

interface LayoutProps {
  children: ReactNode;
  lang: string;
  setLang: (lang: string) => void;
}

export default function Layout({ children, lang, setLang }: LayoutProps) {
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [hasOpenedSearch, setHasOpenedSearch] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const getLocalizedPath = (path: string, targetLang: string) => {
    // Remove existing prefix if any
    const cleanPath = location.pathname.replace(/^\/(ja|id)/, '') || '/';
    
    if (targetLang === 'EN') return cleanPath;
    const prefix = targetLang === 'JP' ? '/ja' : '/id';
    return `${prefix}${cleanPath === '/' ? '' : cleanPath}`;
  };

  const l = (path: string) => {
    if (lang === 'EN') return path;
    const prefix = lang === 'JP' ? '/ja' : '/id';
    return `${prefix}${path === '/' ? '' : path}`;
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(true);
        setHasOpenedSearch(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const t = translations[lang];
  const baseUrl = "https://faizizahari.com";
  const cleanPath = location.pathname.replace(/^\/(ja|id)/, '') || '/';
  const canonicalUrl = `${baseUrl}${cleanPath}`;

  return (
    <div className="min-h-screen flex flex-col font-sans bg-[#fcfaf4]">
      <Helmet>
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" hrefLang="en" href={`${baseUrl}${cleanPath}`} />
        <link rel="alternate" hrefLang="ja" href={`${baseUrl}/ja${cleanPath === '/' ? '' : cleanPath}`} />
        <link rel="alternate" hrefLang="id" href={`${baseUrl}/id${cleanPath === '/' ? '' : cleanPath}`} />
        <link rel="alternate" hrefLang="x-default" href={`${baseUrl}${cleanPath}`} />
        <link rel="alternate" type="text/plain" title="LLMS" href={`${baseUrl}/llms.txt`} />
      </Helmet>

      {/* Navbar */}
      <header className="sticky top-0 z-50 bg-[#fcfaf4]/80 backdrop-blur-md border-b border-gray-100/50">
        <nav className="flex items-center justify-between py-6 px-4 md:px-8 max-w-[1200px] w-full mx-auto">
          <Link to={l("/")} className="w-[44px] h-[64px] cursor-pointer block" aria-label="Home - Faizi Zahari Portfolio">
            <img 
              src="https://framerusercontent.com/images/F93p5tG4FOg4a4LALhXkXMmM8.png?width=88&height=129&format=webp&quality=50" 
              alt="Faizi Zahari Logo" 
              className="w-full h-full object-contain"
            />
          </Link>
          
          <div className="hidden md:flex items-center gap-8 text-[15px] font-medium text-gray-500">
            <Link to={l("/about")} className={`hover:text-[#f1b43e] transition-colors cursor-pointer ${location.pathname === l('/about') ? 'text-[#f1b43e]' : ''}`}>{t.about}</Link>
            <Link to={l("/resume")} className={`hover:text-[#f1b43e] transition-colors cursor-pointer ${location.pathname === l('/resume') ? 'text-[#f1b43e]' : ''}`}>{t.resume}</Link>
            <Link to={l("/portfolio")} className={`hover:text-[#f1b43e] transition-colors cursor-pointer ${location.pathname === l('/portfolio') ? 'text-[#f1b43e]' : ''}`}>{t.portfolio}</Link>
            <Link to={l("/blog")} className={`hover:text-[#f1b43e] transition-colors cursor-pointer ${location.pathname === l('/blog') ? 'text-[#f1b43e]' : ''}`}>{t.blog}</Link>
            <Link to={l("/psychometric")} className={`hover:text-[#f1b43e] transition-colors cursor-pointer ${location.pathname === l('/psychometric') ? 'text-[#f1b43e]' : ''}`}>{t.psychometric}</Link>
          </div>

          <div className="flex items-center gap-2 md:gap-4">
            <button 
              onClick={() => { setIsSearchOpen(true); setHasOpenedSearch(true); }}
              className="p-2 hover:bg-gray-200/50 rounded-full transition-colors cursor-pointer"
              aria-label="Search"
            >
              <Search size={20} className="text-[#323232]" />
            </button>
            
            <div className="hidden md:block">
              {/* Language Selector */}
              <div className="relative">
                <button 
                  onClick={() => setIsLangOpen(!isLangOpen)}
                  className="flex items-center gap-2 bg-white px-5 py-2.5 rounded-full shadow-sm border border-gray-100 text-[15px] font-semibold cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <LanguageIcon code={lang} className="w-5 h-5 rounded-sm overflow-hidden" />
                  <span className="ml-0.5">{lang}</span>
                  <ChevronDown size={16} className={`text-gray-400 transition-transform ${isLangOpen ? 'rotate-180' : ''}`} />
                </button>
                
                <AnimatePresence>
                  {isLangOpen && (
                    <motion.div 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="absolute top-full right-0 mt-2 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 w-40 z-50 overflow-hidden"
                    >
                      {languages.map((l) => (
                        <button
                          key={l.code}
                          onClick={() => {
                            const newPath = getLocalizedPath(location.pathname, l.code);
                            setLang(l.code);
                            setIsLangOpen(false);
                            navigate(newPath);
                          }}
                          className={`w-full text-left px-4 py-2.5 text-sm font-medium hover:bg-gray-50 transition-colors flex items-center gap-3 cursor-pointer ${lang === l.code ? 'text-[#f1b43e]' : 'text-gray-600'}`}
                        >
                          <LanguageIcon code={l.code} className="w-5 h-5 rounded-sm overflow-hidden" />
                          <span>{l.label}</span>
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            <Link to={l("/contact")} className="hidden md:block bg-[#323232] text-white px-5 py-2.5 rounded-full text-[15px] font-medium hover:bg-[#f1b43e] transition-colors shadow-sm whitespace-nowrap cursor-pointer">
              {t.getInTouch}
            </Link>

            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 cursor-pointer z-50 relative"
              aria-label="Toggle Menu"
            >
              {isMenuOpen ? (
                <div className="w-6 h-6 flex items-center justify-center">
                  <span className="absolute w-6 h-0.5 bg-[#323232] rotate-45"></span>
                  <span className="absolute w-6 h-0.5 bg-[#323232] -rotate-45"></span>
                </div>
              ) : (
                <Menu size={24} />
              )}
            </button>
          </div>
        </nav>

        {/* Mobile Menu Overlay */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-[#fcfaf4] border-b border-gray-100 overflow-hidden"
            >
              <div className="flex flex-col p-6 gap-6">
                <div className="flex flex-col gap-4 text-lg font-medium text-gray-500">
                  <Link to={l("/about")} className={`hover:text-[#f1b43e] transition-colors ${location.pathname === l('/about') ? 'text-[#f1b43e]' : ''}`}>{t.about}</Link>
                  <Link to={l("/resume")} className={`hover:text-[#f1b43e] transition-colors ${location.pathname === l('/resume') ? 'text-[#f1b43e]' : ''}`}>{t.resume}</Link>
                  <Link to={l("/portfolio")} className={`hover:text-[#f1b43e] transition-colors ${location.pathname === l('/portfolio') ? 'text-[#f1b43e]' : ''}`}>{t.portfolio}</Link>
                  <Link to={l("/blog")} className={`hover:text-[#f1b43e] transition-colors ${location.pathname === l('/blog') ? 'text-[#f1b43e]' : ''}`}>{t.blog}</Link>
                  <Link to={l("/psychometric")} className={`hover:text-[#f1b43e] transition-colors ${location.pathname === l('/psychometric') ? 'text-[#f1b43e]' : ''}`}>{t.psychometric}</Link>
                </div>

                <div className="h-px bg-gray-100 w-full" />

                <div className="flex flex-col gap-4">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Language</p>
                  <div className="grid grid-cols-1 gap-2">
                    {languages.map((l) => (
                      <button
                        key={l.code}
                        onClick={() => {
                          const newPath = getLocalizedPath(location.pathname, l.code);
                          setLang(l.code);
                          setIsMenuOpen(false);
                          navigate(newPath);
                        }}
                        className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${lang === l.code ? 'bg-[#f1b43e]/10 text-[#f1b43e]' : 'hover:bg-gray-50 text-gray-600'}`}
                      >
                        <LanguageIcon code={l.code} className="w-5 h-5 rounded-sm overflow-hidden" />
                        <span className="font-medium">{l.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <Link 
                  to={l("/contact")} 
                  className="bg-[#323232] text-white px-6 py-4 rounded-2xl text-center font-bold hover:bg-[#f1b43e] transition-colors shadow-sm"
                >
                  {t.getInTouch}
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <main className="flex-1 w-full max-w-[1200px] mx-auto px-4 md:px-8 py-4 min-h-[70vh]">
        {children}
      </main>

      {/* Footer */}
      <Footer t={t} lang={lang} />

      {/* Back to Top Button */}
      <AnimatePresence>
        {showBackToTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            onClick={scrollToTop}
            className="fixed bottom-8 right-8 z-50 bg-[#323232] text-white p-4 rounded-full shadow-lg hover:bg-[#f1b43e] transition-colors group cursor-pointer"
            aria-label="Back to top"
          >
            <ArrowUp size={24} className="group-hover:-translate-y-1 transition-transform" />
          </motion.button>
        )}
      </AnimatePresence>

      <Suspense fallback={null}>
        {hasOpenedSearch && <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />}
      </Suspense>
    </div>
  );
}
